import { cn } from '@/lib/cn';
import Field from 'components/forms/field/Field';
import { defaultWrapperClassName } from 'components/forms/input/Input';
import Select from 'components/forms/select/Select';
import { Day } from 'date-fns';
import { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTimePicker } from './DatePicker';
import {
    modifierOptions,
    RelativeDateTimeValue,
    TIMEFRAME_MODIFIER_MAX_VALUE,
    TIMEFRAME_MODIFIER_MIN_VALUE,
    typeOptions,
    unitOptions
} from './timeframeForm';

type RelativeDateTimeFieldProps = {
    label: string;
    name: string;
    help?: React.ReactNode;
    className?: string;
    weekStartsOn?: Day;
    container?: HTMLElement | null;
    /** Default value to show on specific/fixed date */
    defaultFixedValue?: Date;
};

/**
 * Renders a field with columns of inputs to specify a relative or fixed timeframe with UI.
 * Used to set a single timeframe start/end at a time.
 *
 * Will return an object of the type RelativeDateTimeValue.
 */
const RelativeDateTimeField: React.FC<RelativeDateTimeFieldProps> = ({
    label,
    help,
    name,
    className,
    weekStartsOn = 1,
    defaultFixedValue,
    container
}) => {
    const {
        setValue,
        watch,
        formState: { errors }
    } = useFormContext<{
        [name]: RelativeDateTimeValue;
    }>();
    const currentValue = watch(`${name}`);

    /**
     * Reset some values when the type changes
     */
    const onTypeChange = useCallback(
        (newType: string) => {
            const isCurrentSnapping = currentValue?.type !== 'now' && currentValue?.type !== 'fixed';
            const isNewSnapping = newType !== 'now' && newType !== 'fixed';
            if (isCurrentSnapping !== isNewSnapping || newType === 'now') {
                setValue(`${name}.modifier`, 'exact');
                setValue(`${name}.modifierValue`, undefined);
            }

            if (newType === 'fixed' && defaultFixedValue) {
                setValue(`${name}.date`, defaultFixedValue);
            }
        },
        [setValue, currentValue.type, name, defaultFixedValue]
    );

    const onSnappingUnitChange = useCallback(
        (newUnit: string) => {
            // If swapping snapping unit update modifier unit to match.
            if (currentValue.modifier === 'exact') {
                setValue(`${name}.modifierUnit`, newUnit as RelativeDateTimeValue['unit']);
                setValue(`${name}.modifierValue`, undefined);
            }
        },
        [setValue, currentValue.modifier, name]
    );

    const onModifierValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // allow clearing field
        if (isNaN(e.target.valueAsNumber) || e.target.valueAsNumber === 0) {
            setValue(`${name}.modifierValue`, undefined);
            return;
        }

        setValue(`${name}.modifierValue`, e.target.valueAsNumber);
    };

    return (
        <Field label={label} help={help} className={className}>
            <div className='flex flex-wrap gap-2'>
                <Select name={`${name}.type`} onValueChange={onTypeChange}>
                    <Select.TriggerButton className='w-[140px]' aria-label='Delimiter'>
                        <Select.Value placeholder='type' />
                    </Select.TriggerButton>
                    <Select.Content className='z-10'>
                        {typeOptions.map((group) => (
                            <Select.Group key={group.name}>
                                {group.options.map((option) => (
                                    <Select.Item key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Item>
                                ))}
                            </Select.Group>
                        ))}
                    </Select.Content>
                </Select>
                {currentValue.type === 'fixed' && (
                    <Controller
                        name={`${name}.date`}
                        render={({ field }) => (
                            <DateTimePicker
                                {...field}
                                className='w-[300px]'
                                placeholder='Pick a date and time'
                                weekStartsOn={weekStartsOn}
                                defaultPopupValue={field.value}
                                container={container}
                                granularity='minute'
                                aria-label='Date'
                            />
                        )}
                    />
                )}
                {currentValue.type !== 'now' && currentValue.type !== 'fixed' && (
                    <Select name={`${name}.unit`} onValueChange={onSnappingUnitChange}>
                        <Select.TriggerButton className='w-[112px]' aria-label='Snapping unit'>
                            <Select.Value placeholder='unit' />
                        </Select.TriggerButton>
                        <Select.Content className='z-10'>
                            {unitOptions.map((option) => (
                                <Select.Item key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select>
                )}
                {currentValue.type !== 'fixed' && (
                    <>
                        <Select name={`${name}.modifier`}>
                            <Select.TriggerButton className='w-[108px]' aria-label='Offset'>
                                <Select.Value placeholder='modifier' />
                            </Select.TriggerButton>
                            <Select.Content className='z-10'>
                                {modifierOptions.map((option) => (
                                    <Select.Item key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select>
                        {currentValue.modifier !== 'exact' && (
                            <>
                                <input
                                    min={TIMEFRAME_MODIFIER_MIN_VALUE}
                                    max={TIMEFRAME_MODIFIER_MAX_VALUE}
                                    inputMode='numeric'
                                    name={`${name}.modifierValue`}
                                    aria-label='Offset value'
                                    value={
                                        // value must be stringified to avoid browser taking control of the input
                                        // otherwise will allow leading zeros
                                        currentValue.modifierValue !== undefined ? `${currentValue.modifierValue}` : ''
                                    }
                                    onChange={onModifierValueChange}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                        // Prevent non-numeric input that is allowed by browsers.
                                        if (
                                            e.key === '+' ||
                                            e.key === '-' ||
                                            e.key === '.' ||
                                            e.key.toLowerCase() === 'e'
                                        ) {
                                            e.preventDefault();
                                        }
                                    }}
                                    type='number'
                                    placeholder='0'
                                    step='1'
                                    className={cn(defaultWrapperClassName, 'w-[90px]', {
                                        '!ring-statusErrorPrimary': errors?.[name]?.modifierValue
                                    })}
                                />
                                <Select name={`${name}.modifierUnit`}>
                                    <Select.TriggerButton className='w-[114px]' aria-label='Offset unit'>
                                        <Select.Value placeholder='unit' />
                                    </Select.TriggerButton>
                                    <Select.Content className='z-10'>
                                        {unitOptions.map((option) => (
                                            <Select.Item key={option.value} value={option.value}>
                                                {option.label +
                                                    (currentValue.modifierValue && currentValue.modifierValue === 1
                                                        ? ''
                                                        : 's')}
                                            </Select.Item>
                                        ))}
                                    </Select.Content>
                                </Select>
                            </>
                        )}
                    </>
                )}
            </div>
        </Field>
    );
};

export default RelativeDateTimeField;
